import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { StrategyCard } from "../components/StrategyCard";
import { Plus, Compass, FileDown, Loader2 } from "lucide-react";

export default function StrategiesPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [orgs, setOrgs] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [form, setForm] = useState({ name: "", description: "" });
  const recentKey = `vsm-recent-orgs-${user.id}`;
  const orgId = params.get("org");

  useEffect(() => {
    api.get("/organizations").then(({ data }) => {
      setOrgs(data);
      if (!orgId && data.length) {
        const recent = JSON.parse(localStorage.getItem(recentKey) || "[]");
        const pick = recent.find((id) => data.some((o) => o.id === id)) || data[0].id;
        setParams({ org: pick }, { replace: true });
      }
    });
  }, [orgId, recentKey, setParams]);

  const load = useCallback(() => {
    if (orgId) api.get(`/organizations/${orgId}/strategies`).then(({ data }) => setStrategies(data));
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const selectOrg = (id) => setParams({ org: id });

  useEffect(() => {
    if (!orgId) return;
    const recent = JSON.parse(localStorage.getItem(recentKey) || "[]").filter((r) => r !== orgId);
    localStorage.setItem(recentKey, JSON.stringify([orgId, ...recent].slice(0, 50)));
  }, [orgId, recentKey]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Een strategie moet een naam hebben");
    try {
      await api.post(`/organizations/${orgId}/strategies`, form);
      setForm({ name: "", description: "" });
      toast.success("Strategie aangemaakt");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Aanmaken mislukt");
    }
  };

  const replace = (s) => setStrategies((list) => list.map((x) => (x.id === s.id ? s : x)));
  const remove = (id) => setStrategies((list) => list.filter((x) => x.id !== id));

  const [downloading, setDownloading] = useState(false);
  const downloadHandbook = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/organizations/${orgId}/strategies/handbook.pdf`, { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "Strategie-en-gedragshandboek.pdf";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Handboek downloaden mislukt");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="strategies-page">
      <Header orgs={orgs} currentOrgId={orgId} onSelectOrg={selectOrg} />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between gap-6 mb-8 border-b border-zinc-900 pb-6">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
              <Compass className="h-3.5 w-3.5" /> Centrale repository
            </div>
            <h1 className="font-grotesk text-4xl sm:text-5xl font-bold tracking-tight">Strategieën</h1>
            <p className="text-sm text-zinc-600 mt-2 max-w-xl">
              Een strategie bestaat uit concrete gedragingen. Elke gedraging wordt door AI getoetst op de MARCO-criteria:
              Meetbaar, Actief, Reliable meetbaar, onder Controle en Observeerbaar.
            </p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <Button variant="outline" className="rounded-none h-9 border-zinc-900" onClick={downloadHandbook} disabled={downloading || !strategies.length} data-testid="download-handbook-button">
              {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />} Gedragshandboek (PDF)
            </Button>
            <div className="font-mono text-xs text-zinc-500" data-testid="strategies-count">{strategies.length} strategieën</div>
          </div>
        </div>

        <div className="border border-zinc-900 p-4 mb-10 bg-zinc-50 grid md:grid-cols-[1fr_1.5fr_auto] gap-3 items-start" data-testid="new-strategy-form">
          <Input placeholder="Naam nieuwe strategie" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-zinc-400 bg-white" data-testid="new-strategy-name-input" />
          <Textarea placeholder="Korte omschrijving (optioneel)" rows={1} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none border-zinc-400 bg-white min-h-9" data-testid="new-strategy-description-input" />
          <Button className="rounded-none bg-zinc-900 hover:bg-zinc-700" onClick={create} data-testid="create-strategy-button">
            <Plus className="h-4 w-4 mr-1" /> Strategie
          </Button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {strategies.map((s, i) => (
            <StrategyCard key={s.id} strategy={s} index={i} onChange={replace} onDeleted={remove} allStrategies={strategies} />
          ))}
        </div>
        {strategies.length === 0 && <p className="text-zinc-400 text-sm" data-testid="no-strategies">Nog geen strategieën voor deze organisatie.</p>}
      </main>
    </div>
  );
}
